import { Component, OnInit, NgZone, DestroyRef } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { CommonModule } from "@angular/common";
import {
  FormsModule,
  ReactiveFormsModule,
  FormGroup,
  FormBuilder,
  Validators,
} from "@angular/forms";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatInputModule } from "@angular/material/input";
import { MatButtonModule } from "@angular/material/button";
import { provideNativeDateAdapter } from "@angular/material/core";
import { HeaderComponent } from "../header/header.component";
import { Router, ActivatedRoute } from "@angular/router";
import {
  WhatsAppService,
  WhatsAppCampaignRequest,
} from "src/app/service/whatsapp.service";

export interface TextField {
  id: string;
  value: string;
  type: "text" | "textarea";
}

export interface VariableField {
  id: string;
  label: string;
  value: string;
}

export interface ActionButton {
  id: string;
  text: string;
}

export interface CampaignTemplate {
  image?: string;
  textFields: TextField[];
}

export interface UsedTemplate {
  _id: string;
  templateName: string;
  projectId: string;
  headerType: string;
  headerMediaUrl: string;
  paraCount: number;
  paras: string[];
  buttons: string[];
  createdById: string;
  createdByName: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

@Component({
    selector: "app-whatsapp-broadcast",
    templateUrl: "./whatsapp-broadcast.component.html",
    styleUrls: ["./whatsapp-broadcast.component.css"],
    providers: [provideNativeDateAdapter()],
    imports: [
        HeaderComponent,
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatDatepickerModule,
        MatInputModule,
        MatButtonModule,
    ]
})
export class WhatsappBroadcastComponent implements OnInit {
  // Track by function for conversation messages to prevent re-renders
  trackByMessageId(index: number, msg: any): string {
    return msg._id || `${msg.createdAt}_${index}`;
  }

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private whatsappService: WhatsAppService,
    private fb: FormBuilder,
    private ngZone: NgZone,
    private destroyRef: DestroyRef,
  ) {}

  activeTab: string = "create";
  uploadedImage: string | null = null;
  uploadedFile: File | null = null;
  uploadedUserList: File | null = null;

  // Templates
  usedTemplates: UsedTemplate[] = [];
  templatesLoading: boolean = false;
  templatesError: string = "";

  // Campaign Metrics
  campaigns: any[] = [];
  metricsLoading: boolean = false;
  metricsError: string = "";
  currentPage: number = 1;
  itemsPerPage: number = 10;
  totalItems: number = 0;
  isLoadingMore: boolean = false;

  // Campaign Users Detail
  selectedCampaign: any = null;
  campaignUsers: any[] = [];
  campaignUsersLoading: boolean = false;
  campaignUsersError: string = "";

  // Fixed template parts (non-editable)
  private readonly fixedTemplate = {
    greeting:
      'Hi {user_name},\n\nMost real estate sales today still rely on buyer’s imagination. But when you’re selling a home worth crores, "imagining" isn\'t enough: Certainty is what closes the deal.\n\n',
    closing:
      "\n\nReady to see the tech in action?\nPick a time for a quick 10-minute demo or drop us a line",
  };

  // Current logged-in user name
  currentUserName: string = "";

  // Dynamic variable fields (starts with 1, max 3)
  variableFields: VariableField[] = [
    { id: "1", label: "Paragraph 1", value: "" },
  ];

  // WhatsApp Action Buttons (for preview only - fixed 2 buttons)
  actionButtons: ActionButton[] = [
    { id: "1", text: "📅 Book A Demo" },
    { id: "2", text: "✉️ Quick Enquiry (Email)" },
  ];

  textFields: TextField[] = [];

  // Preview data
  previewData: CampaignTemplate = {
    textFields: [],
  };

  // Form for campaign data
  campaignForm: FormGroup;
  isLoading: boolean = false;
  errorMessage: string = "";
  successMessage: string = "";
  showSuccessState: boolean = false;

  // Template tracking
  currentTemplateId: string | null = null;
  parasModified: boolean = false;

  // Delete confirmation modal
  showDeleteModal: boolean = false;
  templateToDelete: UsedTemplate | null = null;
  isDeleting: boolean = false;

  // Messages Tab
  messages: any[] = [];
  messagesLoading: boolean = false;
  messagesError: string = "";
  messagesCurrentPage: number = 1;
  messagesItemsPerPage: number = 25;
  messagesTotalItems: number = 0;
  messagesStartDate: Date | null = null;
  messagesEndDate: Date | null = null;
  today: Date = new Date();
  private messagesStartDateStr: string = "";
  private messagesEndDateStr: string = "";

  // Conversation Detail View
  selectedConversation: any = null;
  conversationMessages: any[] = [];
  conversationLoading: boolean = false;
  conversationError: string = "";
  conversationCurrentPage: number = 1;
  conversationItemsPerPage: number = 20;

  ngOnInit(): void {
    this.initForm();
    this.currentUserName = localStorage.getItem("username") || "User";
    this.updatePreview();

    // Check for URL parameters to open specific conversation
    this.activatedRoute.queryParams.subscribe((params) => {
      const panel = params['panel'];
      const phoneNumber = params['phoneNumber'];

      if (panel === 'messages' && phoneNumber) {
        // Switch to messages tab and load the conversation
        this.activeTab = 'messages';
        this.loadIncomingMessages(true);
        // Create a mock message object for the conversation
        this.loadConversationByPhoneNumber(phoneNumber);
      } else {
        this.activeTab = "create";
      }
    });
  }

  // Get the complete message with variables replaced
  get completeMessage(): string {
    const values = this.variableFields.map((f) => f.value || "P" + f.id);
    const middleText = values.join("\n\n");
    const greetingWithName = this.fixedTemplate.greeting.replace(
      "{user_name}",
      this.currentUserName,
    );
    return greetingWithName + middleText + this.fixedTemplate.closing;
  }

  private initForm(): void {
    this.campaignForm = this.fb.group({
      campaignName: ["", [Validators.required, Validators.minLength(3)]],
      headerMedia: [null],
      headerType: ["image"],
      paraCount: [2],
      paras: [[], Validators.required],
      userListExcel: [null],
    });
  }

  get campaignName() {
    return this.campaignForm.get("campaignName");
  }

  onTabChange(tab: string): void {
    this.activeTab = tab;
    // Clear messages when switching tabs to prevent cross-tab message leakage
    this.errorMessage = "";
    this.successMessage = "";
    if (tab === "templates") {
      this.loadTemplates();
    } else if (tab === "metrics") {
      this.loadCampaigns(true); // Reset and load campaigns
    } else if (tab === "messages") {
      this.loadIncomingMessages(true);
    }
  }

  loadTemplates(): void {
    this.templatesLoading = true;
    this.templatesError = "";

    this.whatsappService.getUsedTemplates().subscribe({
      next: (response: any) => {
        this.templatesLoading = false;
        // The API returns data directly as an array
        if (response && response.data && Array.isArray(response.data)) {
          this.usedTemplates = response.data;
        } else {
          this.usedTemplates = [];
          this.templatesError = "Invalid response format";
        }
      },
      error: (error) => {
        this.templatesLoading = false;
        console.error("Error loading templates:", error);
        this.templatesError =
          error.error?.message?.message ||
          error.error?.message ||
          "Failed to load templates. Please try again.";
        this.usedTemplates = [];
      },
    });
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      const file = input.files[0];

      // Validate file type - only JPG, JPEG, or PNG
      const validTypes = ["image/jpeg", "image/jpg", "image/png"];
      if (!validTypes.includes(file.type)) {
        this.errorMessage = "Image must be JPG, JPEG or PNG format";
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        this.errorMessage = "Image size must be less than 5MB";
        return;
      }

      this.uploadedFile = file;
      this.campaignForm.patchValue({ headerMedia: file });
      this.errorMessage = "";

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        this.uploadedImage = e.target?.result as string;
        this.previewData.image = this.uploadedImage;
      };
      reader.readAsDataURL(file);
    }
  }

  onUserListFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      const file = input.files[0];

      // Validate file type (Excel files)
      const validTypes = [
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel",
      ];
      if (
        !validTypes.includes(file.type) &&
        !file.name.endsWith(".xlsx") &&
        !file.name.endsWith(".xls")
      ) {
        this.errorMessage = "Please select a valid Excel file (.xlsx or .xls)";
        return;
      }

      this.uploadedUserList = file;
      this.campaignForm.patchValue({ userListExcel: file });
      this.errorMessage = "";
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    if (event.dataTransfer?.files && event.dataTransfer.files[0]) {
      const file = event.dataTransfer.files[0];

      // Validate file type - only JPG, JPEG, or PNG
      const validTypes = ["image/jpeg", "image/jpg", "image/png"];
      if (!validTypes.includes(file.type)) {
        this.errorMessage = "Image must be JPG, JPEG or PNG format";
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        this.errorMessage = "Image size must be less than 5MB";
        return;
      }

      this.uploadedFile = file;
      this.campaignForm.patchValue({ headerMedia: file });
      this.errorMessage = "";

      const reader = new FileReader();
      reader.onload = (e) => {
        this.uploadedImage = e.target?.result as string;
        this.previewData.image = this.uploadedImage;
      };
      reader.readAsDataURL(file);
    }
  }

  addTextField(): void {
    // Not used anymore with fixed template
  }

  addVariableField(): void {
    if (this.variableFields.length >= 3) {
      return;
    }
    const nextId = this.variableFields.length + 1;
    this.variableFields.push({
      id: nextId.toString(),
      label: "Paragraph " + nextId,
      value: "",
    });
    this.updatePreview();
  }

  removeVariableField(id: string): void {
    if (this.variableFields.length <= 1) {
      return;
    }
    this.variableFields = this.variableFields.filter((f) => f.id !== id);
    // Re-label remaining fields
    this.variableFields.forEach((field, index) => {
      field.label = "Paragraph " + (index + 1);
      field.id = (index + 1).toString();
    });
    this.updatePreview();
  }

  onVariableChange(id: string, value: string): void {
    const field = this.variableFields.find((f) => f.id === id);
    if (field) {
      field.value = value;
      this.parasModified = true;
      this.updatePreview();
    }
  }

  getNonButtonFields(): TextField[] {
    return this.textFields.filter((f) => !this.isButtonField(f));
  }

  removeTextField(id: string): void {
    const nonButtonFields = this.getNonButtonFields();
    // Don't allow removing if it's the last non-button field
    if (this.isButtonField({ id } as TextField) || nonButtonFields.length > 1) {
      this.textFields = this.textFields.filter((field) => field.id !== id);
      this.updatePreview();
      this.updateParaCount();
    }
  }

  onTextFieldChange(id: string, value: string): void {
    const field = this.textFields.find((f) => f.id === id);
    if (field) {
      field.value = value;
      this.updatePreview();
      this.updateParasValue();
    }
  }

  private updateParaCount(): void {
    this.campaignForm.patchValue({ paraCount: this.textFields.length });
  }

  private updateParasValue(): void {
    const paras = this.textFields
      .map((field) => field.value.trim())
      .filter((value) => value.length > 0);
    this.campaignForm.patchValue({ paras });
  }

  updatePreview(): void {
    // Preview updated via completeMessage getter
  }

  getTemplateMessagePreview(): string {
    return this.completeMessage;
  }

  private validateForm(): boolean {
    if (!this.campaignName?.value) {
      this.errorMessage = "Please enter a campaign name";
      return false;
    }

    if (!this.uploadedUserList) {
      this.errorMessage = "Please upload the user list Excel file";
      return false;
    }

    this.errorMessage = "";
    return true;
  }

  saveCampaign(): void {
    if (!this.validateForm()) {
      return;
    }

    this.isLoading = true;
    this.successMessage = "";
    this.errorMessage = "";
    this.showSuccessState = false;

    const formData = this.prepareFormData();

    this.whatsappService.createWhatsAppTemplate(formData).subscribe({
      next: (response) => {
        this.isLoading = false;
        if (response.success) {
          this.successMessage = "Campaign saved successfully!";
          this.showSuccessState = true;
          setTimeout(() => {
            this.resetForm();
            this.showSuccessState = false;
          }, 2000);
        } else {
          this.errorMessage = response.message || "Failed to save campaign";
        }
      },
      error: (error) => {
        this.isLoading = false;
        console.error("Error saving campaign:", error);
        this.errorMessage =
          error.error?.message || "Failed to save campaign. Please try again.";
      },
    });
  }

  triggerCampaign(): void {
    if (!this.validateForm()) {
      return;
    }

    this.isLoading = true;
    this.successMessage = "";
    this.errorMessage = "";
    this.showSuccessState = false;

    const formData = this.prepareFormData();

    this.whatsappService.createWhatsAppCampaign(formData).subscribe({
      next: (response: any) => {
        this.isLoading = false;
        // Check for success based on status code or message
        if (response.statusCode === 200 || response.message === "Successful") {
          this.successMessage =
            "Campaign created successfully! You can create another campaign or navigate away.";
          this.showSuccessState = true;
          setTimeout(() => {
            this.resetForm();
            this.showSuccessState = false;
            // Keep user on the page to create more campaigns
            this.successMessage = "";
          }, 3000);
        } else {
          this.errorMessage = response.message || "Failed to create campaign";
        }
      },
      error: (error) => {
        this.isLoading = false;
        console.error("Error creating campaign:", error);

        // Extract the actual error message from the response
        let errorMsg = "Failed to create campaign. Please try again.";
        if (error.error?.message?.message) {
          errorMsg = error.error.message.message;
        } else if (error.error?.message) {
          errorMsg = error.error.message;
        } else if (error.message) {
          errorMsg = error.message;
        }

        this.errorMessage = errorMsg;
      },
    });
  }

  private prepareFormData(): FormData {
    const formData = new FormData();

    // Add campaign name
    formData.append("campaignName", this.campaignForm.value.campaignName);

    // Add static projectId
    formData.append("projectId", "V-ESTATE");

    // Add template ID if using a template and paragraphs were not modified
    if (this.currentTemplateId && !this.parasModified) {
      formData.append("_id", this.currentTemplateId);
    }

    // Add header media if present
    if (this.uploadedFile) {
      formData.append("headerMedia", this.uploadedFile);
      formData.append("headerType", "image");
    } else {
      formData.append("headerType", "none");
    }

    // Add the paragraphs that have content
    const validParas = this.variableFields
      .filter((f) => f.value.trim().length > 0)
      .map((f) => f.value.trim());

    formData.append("paraCount", validParas.length.toString());

    validParas.forEach((para) => {
      formData.append("paras", para);
    });

    // Add user list Excel file
    if (this.uploadedUserList) {
      formData.append("userListExcel", this.uploadedUserList);
    }

    return formData;
  }

  removeImage(): void {
    this.uploadedImage = null;
    this.uploadedFile = null;
    if (this.previewData) {
      this.previewData.image = undefined;
    }
    this.campaignForm.patchValue({ headerMedia: null });
  }

  removeUserList(): void {
    this.uploadedUserList = null;
    this.campaignForm.patchValue({ userListExcel: null });
  }

  resetForm(): void {
    this.campaignForm.reset();
    this.uploadedImage = null;
    this.uploadedFile = null;
    this.uploadedUserList = null;
    this.variableFields = [{ id: "1", label: "Paragraph 1", value: "" }];
    this.actionButtons = [
      { id: "1", text: "📅 Book A Demo" },
      { id: "2", text: "✉️ Quick Enquiry (Email)" },
    ];
    this.textFields = [];
    this.previewData = {
      textFields: [],
    };
    this.errorMessage = "";
    this.successMessage = "";
    this.currentTemplateId = null;
    this.parasModified = false;
    this.updateParasValue();
    this.campaignForm.markAsUntouched();
    this.campaignForm.markAsPristine();
  }

  goBack(): void {
    this.router.navigate(["/home"]);
  }

  hasFormContent(): boolean {
    return !!(
      this.campaignName?.value?.trim() ||
      this.uploadedImage ||
      this.uploadedUserList ||
      this.hasVariableContent
    );
  }

  get hasTextContent(): boolean {
    return this.textFields.some((f) => f.value.trim().length > 0);
  }

  getTextFieldsWithValue(): TextField[] {
    return this.textFields.filter((f) => f.value.trim().length > 0);
  }

  isButtonField(field: TextField): boolean {
    return false;
  }

  get hasVariableContent(): boolean {
    return this.variableFields.some((f) => f.value.trim().length > 0);
  }

  useTemplate(template: UsedTemplate): void {
    // Store template ID and reset modification flag
    this.currentTemplateId = template._id;
    this.parasModified = false;

    // Set campaign name
    this.campaignForm.patchValue({
      campaignName: template.templateName + " (Copy)",
    });

    // Load header media if exists
    if (template.headerMediaUrl && template.headerType !== "none") {
      this.uploadedImage = template.headerMediaUrl;
      this.previewData.image = template.headerMediaUrl;
    } else {
      this.uploadedImage = null;
      this.previewData.image = undefined;
    }

    // Load paragraphs
    if (template.paras && template.paras.length > 0) {
      // Clear existing variable fields and match the template's para count
      this.variableFields = [];
      for (let i = 0; i < Math.min(template.paras.length, 3); i++) {
        this.variableFields.push({
          id: (i + 1).toString(),
          label: "Paragraph " + (i + 1),
          value: template.paras[i] || "",
        });
      }
      // Fill remaining fields if template has less than 3
      while (this.variableFields.length < 3) {
        const nextId = this.variableFields.length + 1;
        this.variableFields.push({
          id: nextId.toString(),
          label: "Paragraph " + nextId,
          value: "",
        });
      }
    }

    // Switch to create tab
    this.activeTab = "create";
    this.updatePreview();
  }

  // Get complete message for template with fixed greeting and closing
  getTemplateCompleteMessage(template: UsedTemplate): string {
    const middleText =
      template.paras && template.paras.length > 0
        ? template.paras.join("\n\n")
        : "";
    const greetingWithName = this.fixedTemplate.greeting.replace(
      "{user_name}",
      this.currentUserName,
    );
    return greetingWithName + middleText + this.fixedTemplate.closing;
  }

  deleteTemplate(templateId: string): void {
    // Find the template to display in modal
    const template = this.usedTemplates.find((t) => t._id === templateId);
    if (template) {
      this.templateToDelete = template;
      this.showDeleteModal = true;
    }
  }

  confirmDeleteTemplate(): void {
    if (!this.templateToDelete) return;

    const templateId = this.templateToDelete._id;
    const deletedTemplate = this.templateToDelete;

    // Optimistic update: remove from list immediately
    this.usedTemplates = this.usedTemplates.filter((t) => t._id !== templateId);
    this.isDeleting = true;

    this.whatsappService.deleteTemplate(templateId).subscribe({
      next: (response: any) => {
        this.isDeleting = false;
        if (response.success) {
          // Success - keep the optimistic update and close modal
          this.closeDeleteModal();
        } else {
          // Failure - restore the template and show error
          this.usedTemplates = [...this.usedTemplates, deletedTemplate];
          this.errorMessage = response.message || "Failed to delete template";
          this.closeDeleteModal();
        }
      },
      error: (error) => {
        this.isDeleting = false;
        console.error("Error deleting template:", error);
        // Restore the template on error
        this.usedTemplates = [...this.usedTemplates, deletedTemplate];
        this.errorMessage =
          error.error?.message ||
          "Failed to delete template. Please try again.";
        this.closeDeleteModal();
      },
    });
  }

  closeDeleteModal(): void {
    this.showDeleteModal = false;
    this.templateToDelete = null;
    this.isDeleting = false;
  }

  loadCampaigns(reset: boolean = false): void {
    // For infinite scroll, we only load on first call or reset
    if (this.metricsLoading && !reset) return;

    if (reset) {
      this.currentPage = 1;
      this.campaigns = [];
      this.metricsLoading = true;
      this.isLoadingMore = false;
    } else if (this.campaigns.length === 0) {
      this.metricsLoading = true;
      this.isLoadingMore = false;
    } else {
      // Loading more pages
      this.isLoadingMore = true;
    }

    this.metricsError = "";

    this.whatsappService
      .getCampaigns(this.currentPage, this.itemsPerPage)
      .subscribe({
        next: (response: any) => {
          this.metricsLoading = false;
          this.isLoadingMore = false;
          if (response && response.data && Array.isArray(response.data)) {
            if (reset) {
              this.campaigns = response.data;
            } else {
              this.campaigns = [...this.campaigns, ...response.data];
            }
            this.totalItems = this.campaigns.length;
          } else {
            this.campaigns = [];
            this.metricsError = "Invalid response format";
          }
        },
        error: (error) => {
          this.metricsLoading = false;
          this.isLoadingMore = false;
          console.error("Error loading campaigns:", error);
          this.metricsError =
            error.error?.message?.message ||
            error.error?.message ||
            "Failed to load campaigns. Please try again.";
          this.campaigns = [];
        },
      });
  }

  onScroll(event: Event): void {
    const element = event.target as HTMLElement;
    const scrollTop = element.scrollTop;
    const scrollHeight = element.scrollHeight;
    const clientHeight = element.clientHeight;

    // Load more when scrolled to 100px from bottom, only if not already loading
    if (
      scrollHeight - scrollTop - clientHeight < 100 &&
      !this.metricsLoading &&
      !this.isLoadingMore
    ) {
      this.currentPage++;
      this.loadCampaigns();
    }
  }

  viewCampaignUsers(campaign: any): void {
    this.selectedCampaign = campaign;
    this.loadCampaignUsers();
  }

  loadCampaignUsers(): void {
    if (!this.selectedCampaign) return;

    this.campaignUsersLoading = true;
    this.campaignUsersError = "";

    this.whatsappService.getCampaignUsers(this.selectedCampaign._id).subscribe({
      next: (response: any) => {
        this.campaignUsersLoading = false;
        if (response && response.data && Array.isArray(response.data)) {
          this.campaignUsers = response.data;
        } else {
          this.campaignUsers = [];
          this.campaignUsersError = "Invalid response format";
        }
      },
      error: (error) => {
        this.campaignUsersLoading = false;
        console.error("Error loading campaign users:", error);
        this.campaignUsersError =
          error.error?.message?.message ||
          error.error?.message ||
          "Failed to load campaign users. Please try again.";
        this.campaignUsers = [];
      },
    });
  }

  backToCampaigns(): void {
    this.selectedCampaign = null;
    this.campaignUsers = [];
    this.campaignUsersError = "";
  }

  loadIncomingMessages(reset: boolean = false): void {
    if (this.messagesLoading && !reset) return;

    if (reset) {
      this.messagesCurrentPage = 1;
      this.messages = [];
      this.messagesLoading = true;
    } else {
      this.messagesLoading = true;
    }

    this.messagesError = "";

    this.whatsappService.getIncomingMessages(
      this.messagesCurrentPage,
      this.messagesItemsPerPage,
      this.messagesStartDateStr || undefined,
      this.messagesEndDateStr || undefined
    ).subscribe({
      next: (response: any) => {
        this.messagesLoading = false;
        if (response && response.data && Array.isArray(response.data)) {
          if (reset) {
            this.messages = response.data;
          } else {
            this.messages = [...this.messages, ...response.data];
          }
          this.messagesTotalItems = response.total || this.messages.length;
        } else {
          this.messages = [];
          this.messagesError = "Invalid response format";
        }
      },
      error: (error) => {
        this.messagesLoading = false;
        console.error("Error loading messages:", error);
        this.messagesError =
          error.error?.message?.message ||
          error.error?.message ||
          "Failed to load messages. Please try again.";
        this.messages = [];
      },
    });
  }

  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  applyMessagesDateFilter(): void {
    if (!this.messagesStartDate && !this.messagesEndDate) {
      return;
    }
    // If only one side of the range was picked, treat it as a single-day filter
    if (!this.messagesEndDate) {
      this.messagesEndDate = this.messagesStartDate;
    }
    if (!this.messagesStartDate) {
      this.messagesStartDate = this.messagesEndDate;
    }
    this.messagesStartDateStr = this.formatDate(this.messagesStartDate);
    this.messagesEndDateStr = this.formatDate(this.messagesEndDate);
    this.loadIncomingMessages(true);
  }

  clearMessagesDateFilter(): void {
    this.messagesStartDate = null;
    this.messagesEndDate = null;
    this.messagesStartDateStr = "";
    this.messagesEndDateStr = "";
    this.loadIncomingMessages(true);
  }

  onMessagesScroll(event: Event): void {
    const element = event.target as HTMLElement;
    const scrollTop = element.scrollTop;
    const scrollHeight = element.scrollHeight;
    const clientHeight = element.clientHeight;

    // Load more when scrolled to 100px from bottom, only if not already loading
    if (
      scrollHeight - scrollTop - clientHeight < 100 &&
      !this.messagesLoading
    ) {
      this.messagesCurrentPage++;
      this.loadIncomingMessages();
    }
  }

  viewConversation(message: any): void {
    this.selectedConversation = message;
    this.loadConversationMessages();
  }

  loadConversationMessages(): void {
    if (!this.selectedConversation) return;

    this.conversationLoading = true;
    this.conversationError = "";
    this.conversationCurrentPage = 1;
    this.conversationMessages = [];

    this.whatsappService.getMessagesByPhoneNumber(
      this.selectedConversation.phoneNumber,
      this.conversationCurrentPage,
      this.conversationItemsPerPage
    ).subscribe({
      next: (response: any) => {
        this.conversationLoading = false;
        if (response && response.data && Array.isArray(response.data)) {
          this.conversationMessages = response.data;
        } else {
          this.conversationMessages = [];
          this.conversationError = "Invalid response format";
        }
      },
      error: (error) => {
        this.conversationLoading = false;
        console.error("Error loading conversation:", error);
        this.conversationError =
          error.error?.message?.message ||
          error.error?.message ||
          "Failed to load conversation. Please try again.";
        this.conversationMessages = [];
      },
    });
  }

  onConversationScroll(event: Event): void {
    // Scroll handler removed - no pagination on scroll
    // All messages are loaded at once without scroll-based pagination
  }

  backToMessages(): void {
    this.selectedConversation = null;
    this.conversationMessages = [];
    this.conversationError = "";
  }

  loadConversationByPhoneNumber(phoneNumber: string): void {
    // Create a mock selected conversation object
    this.selectedConversation = {
      phoneNumber: phoneNumber,
      fromName: phoneNumber, // Will show phone number as name initially
      phoneNumberId: phoneNumber
    };

    // Load the conversation messages
    this.loadConversationMessages();
  }

  getDirectionClass(direction: string): string {
    switch (direction) {
      case "INCOMING":
        return "incoming";
      case "OUTGOING":
        return "outgoing";
      case "OUTGOING-BOT":
        return "outgoing-bot";
      case "OUTGOING-CAMPAIGN":
        return "outgoing-campaign";
      default:
        return "unknown";
    }
  }

  getSenderLabel(message: any): string {
    switch (message.direction) {
      case "INCOMING":
        return this.selectedConversation?.fromName || this.selectedConversation?.phoneNumber || "Contact";
      case "OUTGOING":
        return "You";
      case "OUTGOING-BOT":
        return "Bot";
      case "OUTGOING-CAMPAIGN":
        return "Campaign";
      default:
        return "Unknown";
    }
  }

  getMessageDisplayText(message: any): string {
    if (message.messageType === "text") {
      return message.messageBody || "";
    } else if (message.fileUrl) {
      return `[${message.messageType.toUpperCase()}] ${message.fileUrl}`;
    }
    return message.messageBody || "";
  }

  formatWhatsAppTimestamp(timestamp: string): string {
    // Convert timestamp string to readable format
    const date = new Date(timestamp);
    return date.toLocaleString();
  }

  copyPhoneNumber(phoneNumber: string, event: Event): void {
    event.stopPropagation(); // Prevent triggering row click
    navigator.clipboard.writeText(phoneNumber).then(() => {
      // Show toast notification
      this.showToast("Phone number copied to clipboard!");
    }).catch((err) => {
      console.error("Failed to copy phone number:", err);
      this.showToast("Failed to copy phone number", "error");
    });
  }

  // Toast notification
  showToast(message: string, type: "success" | "error" = "success"): void {
    console.log("Showing toast:", message, type);

    // Remove existing toast if any
    const existingToast = document.querySelector(".copy-toast");
    if (existingToast) {
      existingToast.remove();
    }

    // Create toast element with inline styles to bypass View Encapsulation
    const toast = document.createElement("div");
    toast.className = `copy-toast copy-toast-${type}`;

    // Apply inline styles directly to the element
    Object.assign(toast.style, {
      position: "fixed",
      bottom: "2rem",
      right: "2rem",
      display: "flex",
      alignItems: "center",
      gap: "0.75rem",
      padding: "1rem 1.25rem",
      backgroundColor: "#fff",
      borderRadius: "8px",
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
      zIndex: "999999",
      opacity: "0",
      transform: "translateY(20px)",
      transition: "all 0.3s ease-out",
      pointerEvents: "none",
      minWidth: "280px",
      borderLeft: type === "success" ? "4px solid #22c55e" : "4px solid #ef4444",
      background: type === "success"
        ? "linear-gradient(to right, #f0fdf4, #fff)"
        : "linear-gradient(to right, #fef2f2, #fff)"
    });

    // Create icon
    const icon = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    icon.setAttribute("width", "20");
    icon.setAttribute("height", "20");
    icon.setAttribute("viewBox", "0 0 24 24");
    Object.assign(icon.style, {
      flexShrink: "0",
      fill: "currentColor",
      color: type === "success" ? "#22c55e" : "#ef4444"
    });

    const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
    if (type === "success") {
      path.setAttribute("d", "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z");
    } else {
      path.setAttribute("d", "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z");
    }
    icon.appendChild(path);

    // Create message span
    const messageSpan = document.createElement("span");
    messageSpan.textContent = message;
    Object.assign(messageSpan.style, {
      fontSize: "0.9rem",
      color: "#374151",
      fontWeight: "500"
    });

    toast.appendChild(icon);
    toast.appendChild(messageSpan);

    document.body.appendChild(toast);
    console.log("Toast appended to body:", toast);
    console.log("Toast computed style:", window.getComputedStyle(toast).cssText);

    // Trigger animation
    requestAnimationFrame(() => {
      toast.style.opacity = "1";
      toast.style.transform = "translateY(0)";
      toast.style.pointerEvents = "auto";
      console.log("Toast show styles applied");
    });

    // Remove after 2 seconds
    setTimeout(() => {
      toast.style.opacity = "0";
      toast.style.transform = "translateY(20px)";
      console.log("Toast hide styles applied");
      setTimeout(() => toast.remove(), 300);
    }, 2000);
  }
}
